import { cx } from '@/lib/cx';
import styles from './Brand.module.css';

/*
 * A marca virou arte, não mais tipografia. O wordmark é um script desenhado com a bandeira e o
 * louro em dourado — nada disso se reproduz com uma fonte de texto, então Wordmark e BrandLogo
 * servem os arquivos do projeto de design em vez de compor a marca em CSS.
 *
 * Os dois assets são reversos (claros sobre transparente) porque, no app, a marca só aparece
 * sobre o verde: cabeçalho, splash, login e o cartão de compartilhamento. A versão positiva
 * existe para papel claro e entra apenas no showcase, onde a própria marca é o assunto.
 */

const WORDMARK_SRC = '/brand/pintrack-wordmark-reverse.png';
const LOGO_REVERSE_SRC = '/brand/pintrack-logo-reverse.png';
const LOGO_POSITIVE_SRC = '/brand/pintrack-logo.png';

/**
 * O wordmark sem tagline — a marca em papel de navegação. Dimensionado por **altura**, e não
 * por corpo de fonte: é uma imagem, e a altura é o que mantém o alinhamento com o texto ao lado.
 */
export function Wordmark({ height = 20, className }: { height?: number; className?: string }) {
  return (
    <img
      src={WORDMARK_SRC}
      alt="PinTrack"
      className={cx(styles.wordmark, className)}
      style={{ height }}
      draggable={false}
    />
  );
}

/**
 * O logo inteiro, com a tagline. Só onde a marca é o assunto da tela — splash, login e o
 * showcase. `positive` é a aplicação sobre papel claro: mesma arte, script em verde profundo
 * em vez de creme.
 */
export function BrandLogo({
  height = 96,
  variant = 'reverse',
  className,
}: {
  height?: number;
  variant?: 'reverse' | 'positive';
  className?: string;
}) {
  return (
    <img
      src={variant === 'positive' ? LOGO_POSITIVE_SRC : LOGO_REVERSE_SRC}
      alt="PinTrack — Play. Track. Improve."
      className={cx(styles.logo, className)}
      style={{ height }}
      draggable={false}
    />
  );
}

/** First letters of the first and last words of a display name. "Rafael Moreira" → "RM". */
export function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function Avatar({
  name,
  size = 34,
  variant = 'outline',
  className,
}: {
  name: string;
  size?: number;
  variant?: 'outline' | 'gold' | 'green';
  className?: string;
}) {
  const variantClass = {
    outline: styles.avatarOutline,
    gold: styles.avatarGold,
    green: styles.avatarGreen,
  }[variant];

  return (
    <span
      className={cx(styles.avatar, variantClass, className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.35) }}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </span>
  );
}
